import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUsage } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | error
  const sessionId = params.get("session_id");

  useEffect(() => {
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (!sessionId) {
        setState("error");
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUsage();
          setState("paid");
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts += 1;
      if (attempts >= 12) {
        setState("error");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#050505] text-white font-mono flex items-center justify-center p-6">
      <div className="max-w-md w-full border border-zinc-800 bg-[#0d0d0d] p-10 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto text-volt animate-spin mb-6" size={40} />
            <h1 className="font-display text-3xl tracking-tight mb-2">CONFIRMING PAYMENT</h1>
            <p className="text-sm text-zinc-500">Hang tight — verifying with Stripe…</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-volt mb-6" size={48} />
            <h1 className="font-display text-3xl tracking-tight mb-2">YOU'RE UNLIMITED</h1>
            <p className="text-sm text-zinc-400 mb-8">
              Payment confirmed. Generate as much speech as you want — no limits.
            </p>
            <button
              onClick={() => navigate("/studio")}
              data-testid="back-to-studio-btn"
              className="w-full bg-volt text-black py-3 text-sm font-bold uppercase tracking-widest hover:bg-volt-hover transition-colors duration-100"
            >
              Back to Studio
            </button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="mx-auto text-red-500 mb-6" size={48} />
            <h1 className="font-display text-3xl tracking-tight mb-2">STILL PROCESSING</h1>
            <p className="text-sm text-zinc-400 mb-8">
              We couldn't confirm the payment yet. If you completed checkout, it may take a moment —
              your access unlocks automatically.
            </p>
            <button
              onClick={() => navigate("/studio")}
              className="w-full bg-transparent border border-zinc-700 text-white py-3 text-sm font-bold uppercase tracking-widest hover:bg-zinc-800 transition-colors duration-100"
            >
              Return to Studio
            </button>
          </>
        )}
      </div>
    </div>
  );
}
